import React, { useCallback, useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import { dataCatalog } from '../../../../../api/coreData'
import {
  useCreateUserDqlQuery,
  useModifyUserDqlQuery,
  useRemoveUserDqlQuery,
  useFavoriteUserDqlQuery
} from '../../../../../api/denali'
import { useSavedQuery } from './SavedQueryProvider'
import UtilityPanel from './UtilityPanel'
import EditorPanel from './EditorPanel'
import ResultPanel from './ResultPanel'
import ResizableHandle from './ResizablePanels'
import useResizablePanels from './useResizablePanels'
import SaveQueryDialog from './SaveQueryDialog'
import OpenQueryDialog from './OpenQueryDialog'
import QueryToolbar from './QueryToolbar'
import EditorFooter from './EditorFooter'

const useStyles = makeStyles(theme => ({
  editorLayout: {
    height: `calc(100vh - ${theme.layout.appBar.height}px)`,
    width: '100vw',
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden'
  },
  utilityPanel: {
    flexShrink: 0,
    backgroundColor: 'var(--surface-bg-color-d3)',
    overflow: 'auto',
    borderRight: '1px solid var(--color-neutral-600)'
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0
  },
  editorPanel: {
    flexShrink: 0,
    borderBottom: '1px solid var(--color-neutral-600)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  resultPanel: {
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column'
  }
}))

function QueryEditor ({ initialDql }) {
  const classes = useStyles()
  const containerRef = useRef(null)
  const editorRef = useRef(null)
  const saveDialogRef = useRef(null)
  const openDialogRef = useRef(null)
  const { sizes, setUtilityWidth, setEditorHeight } = useResizablePanels()

  const { currentQuery, isDirty, loadQuery, markDirty, markClean, clearQuery, updateCurrentQuery } = useSavedQuery()

  const [currentResult, setCurrentResult] = useState(null)
  const { mutateAsync: queryCatalogView, isLoading: queryProcessing } = dataCatalog.useQueryCatalogViewImperative()

  const { mutateAsync: createQuery, isLoading: creating } = useCreateUserDqlQuery()
  const { mutateAsync: modifyQuery, isLoading: modifying } = useModifyUserDqlQuery()
  const { mutateAsync: removeQuery } = useRemoveUserDqlQuery()
  const { mutateAsync: favoriteQuery, isLoading: favoriting } = useFavoriteUserDqlQuery()

  const isSaving = creating || modifying

  const getEditorValue = useCallback(() => {
    if (editorRef.current && editorRef.current.getValue) {
      return editorRef.current.getValue()
    }
    return ''
  }, [])

  const confirmUnsavedChanges = useCallback(() => {
    if (!isDirty) return true
    return window.confirm('You have unsaved changes. Discard and continue?')
  }, [isDirty])

  // Guard against browser tab close / navigation when there are unsaved changes
  useEffect(() => {
    if (!isDirty) return

    const handler = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  // -- Query execution --
  const handleQuery = useCallback(async () => {
    const queryText = getEditorValue()
    if (!queryText) return

    const result = await queryCatalogView({
      query: { dql: queryText }
    })
    setCurrentResult(result)
  }, [queryCatalogView, getEditorValue])

  // -- File actions --
  const handleNew = useCallback(() => {
    if (!confirmUnsavedChanges()) return
    if (editorRef.current && editorRef.current.setValue) {
      editorRef.current.setValue('')
    }
    clearQuery()
    setCurrentResult(null)
  }, [confirmUnsavedChanges, clearQuery])

  const handleOpen = useCallback(() => {
    if (!confirmUnsavedChanges()) return
    if (openDialogRef.current) {
      openDialogRef.current.open()
    }
  }, [confirmUnsavedChanges])

  const handleSave = useCallback(async () => {
    const dql = getEditorValue()
    if (!dql) return

    if (currentQuery) {
      await modifyQuery({
        userDqlQueryId: currentQuery.userDqlQueryId,
        dql
      })
      updateCurrentQuery({ dql, modifiedDate: new Date().toISOString() })
      markClean()
    } else {
      if (saveDialogRef.current) {
        saveDialogRef.current.open({ mode: 'save' })
      }
    }
  }, [currentQuery, getEditorValue, modifyQuery, updateCurrentQuery, markClean])

  const handleSaveAs = useCallback(() => {
    if (saveDialogRef.current) {
      saveDialogRef.current.open({
        mode: 'saveAs',
        name: currentQuery ? currentQuery.name : '',
        description: currentQuery ? (currentQuery.description || '') : ''
      })
    }
  }, [currentQuery])

  const handleSaveDialogComplete = useCallback(async ({ name, description }) => {
    const dql = getEditorValue()
    if (!dql) return

    const result = await createQuery({ name, description, dql })
    if (result?.userDqlQueryId) {
      loadQuery({
        userDqlQueryId: result.userDqlQueryId,
        name,
        description,
        dql,
        isFavorite: false,
        createdDate: new Date().toISOString(),
        modifiedDate: new Date().toISOString()
      })
    }
  }, [getEditorValue, createQuery, loadQuery])

  const handleDelete = useCallback(async () => {
    if (!currentQuery) return
    if (!window.confirm(`Delete "${currentQuery.name}"? This cannot be undone.`)) return

    await removeQuery({ userDqlQueryId: currentQuery.userDqlQueryId })
    clearQuery()
  }, [currentQuery, removeQuery, clearQuery])

  const handleFavorite = useCallback(async () => {
    if (!currentQuery) return
    const newValue = !currentQuery.isFavorite
    await favoriteQuery({
      userDqlQueryId: currentQuery.userDqlQueryId,
      isFavorite: newValue
    })
    updateCurrentQuery({ isFavorite: newValue })
  }, [currentQuery, favoriteQuery, updateCurrentQuery])

  const handleOpenQuery = useCallback((query) => {
    if (editorRef.current && editorRef.current.setValue) {
      editorRef.current.setValue(query.dql || '')
    }
    loadQuery(query)
    setCurrentResult(null)
  }, [loadQuery])

  const handleEditorChange = useCallback(() => {
    if (!isDirty) {
      markDirty()
    }
  }, [isDirty, markDirty])

  // -- Panel resizing --
  const handleUtilityResize = useCallback((delta) => {
    const newWidth = Math.max(150, Math.min(500, sizes.utilityWidth + delta))
    setUtilityWidth(newWidth)
  }, [sizes.utilityWidth, setUtilityWidth])

  const handleEditorResize = useCallback((delta) => {
    if (!containerRef.current) return

    const containerHeight = containerRef.current.querySelector(`.${classes.mainContent}`).offsetHeight
    const currentHeight = (sizes.editorHeight / 100) * containerHeight
    const newHeight = currentHeight + delta
    const newPercentage = Math.max(20, Math.min(80, (newHeight / containerHeight) * 100))

    setEditorHeight(newPercentage)
  }, [sizes.editorHeight, setEditorHeight, classes.mainContent])

  const handleUtilityReset = useCallback(() => {
    setUtilityWidth(250)
  }, [setUtilityWidth])

  const handleEditorReset = useCallback(() => {
    setEditorHeight(50)
  }, [setEditorHeight])

  const handleInsertText = useCallback((text) => {
    if (editorRef.current && editorRef.current.insertText) {
      editorRef.current.insertText(text)
    }
  }, [])

  const toolbar = (
    <QueryToolbar
      onNew={handleNew}
      onOpen={handleOpen}
      onSave={handleSave}
      onSaveAs={handleSaveAs}
      onDelete={handleDelete}
      onFavorite={handleFavorite}
      onSubmitQuery={handleQuery}
      processing={queryProcessing}
      saving={isSaving}
      favoriting={favoriting}
    />
  )

  return (
    <div className={classes.editorLayout} ref={containerRef}>
      <div className={classes.utilityPanel} style={{ width: sizes.utilityWidth }}>
        <UtilityPanel onInsertText={handleInsertText} />
      </div>
      <ResizableHandle
        direction='horizontal'
        onResize={handleUtilityResize}
        onDoubleClick={handleUtilityReset}
      />
      <div className={classes.mainContent}>
        <div className={classes.editorPanel} style={{ height: `${sizes.editorHeight}%` }}>
          <EditorPanel
            ref={editorRef}
            defaultValue={currentQuery?.dql || initialDql || ''}
            processing={queryProcessing}
            onSubmitQuery={handleQuery}
            onChange={handleEditorChange}
            onSave={handleSave}
            toolbar={toolbar}
            footer={<EditorFooter />}
          />
        </div>
        <ResizableHandle
          direction='vertical'
          onResize={handleEditorResize}
          onDoubleClick={handleEditorReset}
        />
        <div className={classes.resultPanel}>
          <ResultPanel currentResult={currentResult} />
        </div>
      </div>

      <SaveQueryDialog ref={saveDialogRef} onSave={handleSaveDialogComplete} />
      <OpenQueryDialog ref={openDialogRef} onOpen={handleOpenQuery} />
    </div>
  )
}

QueryEditor.propTypes = {
  initialDql: PropTypes.string
}

export default QueryEditor
